import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CreditCard, Crown, Download, ExternalLink, Gift, Image, Share2, Sparkles, Upload } from "lucide-react";
import { ASSETS, STYLES, pageVariants } from "../components/app/constants";
import { SEO } from "../components/shared/SEO";

export const LandingPage = ({ onNavigate }) => {
  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen relative"
    >
      <SEO
        path="/"
        description="Create jaw-dropping AI portraits in seconds. 12 iconic styles: LinkedIn headshots, magazine covers, red carpet glamour, anime, fantasy & more. Upload your selfie, get a masterpiece. 3 free credits."
      />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden pt-20 md:pt-0">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${ASSETS.hero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/90 via-[#050505]/80 to-[#050505]" />
        
        <div className="relative z-10 text-center max-w-4xl mx-auto px-6 pt-8 md:pt-0">
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xs sm:text-sm uppercase tracking-[0.2em] sm:tracking-[0.3em] text-[#D4AF37] mb-4 sm:mb-6 font-semibold"
          >
            AI-Powered Portrait Studio
          </motion.p>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="font-serif text-6xl md:text-8xl tracking-tight leading-[0.9] mb-8"
          >
            Your Image.<br />
            <span className="gold-text glow-text">Elevated.</span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto mb-12"
          >
            Transform any selfie into stunning professional portraits. 
            Magazine covers, red carpet glamour, or championship moments — all in seconds.
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button 
              onClick={() => onNavigate("/start")} 
              className="btn-primary flex items-center justify-center gap-2"
              data-testid="hero-get-started"
            >
              <Sparkles className="w-4 h-4" />
              Start Creating
            </button>
            <button 
              onClick={() => onNavigate("/pricing")} 
              className="btn-secondary"
              data-testid="hero-view-pricing"
            >
              View Pricing
            </button>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border border-white/20 rounded-full flex justify-center pt-2">
            <motion.div 
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1 h-2 bg-[#D4AF37] rounded-full"
            />
          </div>
        </motion.div>
      </section>
      
      {/* Styles Preview Section */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Choose Your Style</p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight">12 Iconic Looks</h2>
          </motion.div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {STYLES.map((style, index) => (
              <motion.div
                key={style.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => onNavigate("/start")}
                className="style-card group"
                data-testid={`style-card-${style.id}`}
              >
                <img src={style.image} alt={style.name} loading="lazy" />
                <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                  <h3 className="font-serif text-2xl mb-1">{style.name}</h3>
                  <p className="text-sm text-white/60">{style.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Before & After Showcase */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">See The Magic</p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight mb-4">From Selfie to Masterpiece</h2>
            <p className="text-lg text-white/50 max-w-2xl mx-auto">Real transformations. Same face, new look. Your identity is always preserved.</p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { before: "https://images.unsplash.com/photo-1661484575641-2253af5e5c2b?w=400&h=500&fit=crop", after: "https://images.unsplash.com/photo-1650398119395-39c4248cfca0?w=400&h=500&fit=crop", style: "Business Executive", tag: "LinkedIn Ready" },
              { before: "https://images.unsplash.com/photo-1572716838914-f6b3c74e589b?w=400&h=500&fit=crop", after: "https://images.unsplash.com/photo-1765005204058-10418f5123c5?w=400&h=500&fit=crop", style: "Magazine Cover", tag: "Editorial Quality" },
              { before: "https://images.unsplash.com/photo-1630480403121-a008d6f1e638?w=400&h=500&fit=crop", after: "https://images.unsplash.com/photo-1680833910988-dc20bc1f8d14?w=400&h=500&fit=crop", style: "Red Carpet", tag: "Hollywood Glamour" }
            ].map((item, idx) => (
              <motion.div
                key={item.style}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className="group relative"
              >
                <div className="relative overflow-hidden rounded-xl border border-white/10">
                  <div className="grid grid-cols-2">
                    <div className="relative">
                      <img src={item.before} alt="Before" className="w-full h-72 object-cover" loading="lazy" />
                      <span className="absolute bottom-2 left-2 bg-black/70 text-white/80 text-xs px-2 py-1 rounded">Before</span>
                    </div>
                    <div className="relative">
                      <img src={item.after} alt="After" className="w-full h-72 object-cover" loading="lazy" />
                      <span className="absolute bottom-2 right-2 bg-[#D4AF37]/90 text-black text-xs px-2 py-1 rounded font-bold">After</span>
                    </div>
                  </div>
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center z-10">
                    <ArrowRight className="w-4 h-4 text-black" />
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <span className="text-[#D4AF37] text-xs uppercase tracking-wider font-medium">{item.tag}</span>
                  <h3 className="font-serif text-xl mt-1">{item.style}</h3>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <button onClick={() => onNavigate("/start")} className="btn-primary px-8" data-testid="see-all-styles-btn">
              Try All 12 Styles Free
            </button>
          </div>
        </div>
      </section>

      {/* Stats Counter */}
      <section className="py-16 px-6 border-y border-white/5">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { value: "12", label: "Portrait Styles" },
            { value: "3", label: "Free Credits" },
            { value: "$0.53", label: "Lowest Price/Portrait" },
            { value: "30s", label: "Generation Time" }
          ].map((stat, idx) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="text-center"
            >
              <p className="font-serif text-4xl md:text-5xl gold-text mb-1">{stat.value}</p>
              <p className="text-white/50 text-sm uppercase tracking-wider">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Where To Use</p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight mb-4">Perfect For Every Platform</h2>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {[
              { name: "LinkedIn", icon: "in", desc: "Stand out to recruiters" },
              { name: "Dating Apps", icon: "heart", desc: "Make a great first impression" },
              { name: "Business Cards", icon: "card", desc: "Professional & polished" },
              { name: "Social Media", icon: "share", desc: "Instagram, TikTok, Twitter" },
              { name: "Website & CV", icon: "globe", desc: "Credible & trustworthy" }
            ].map((useCase, idx) => (
              <motion.div
                key={useCase.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="glass-card p-6 text-center hover:border-[#D4AF37]/30 transition-colors"
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
                  {useCase.icon === "in" && <span className="text-[#D4AF37] font-bold text-lg">in</span>}
                  {useCase.icon === "heart" && <Crown className="w-5 h-5 text-[#D4AF37]" />}
                  {useCase.icon === "card" && <CreditCard className="w-5 h-5 text-[#D4AF37]" />}
                  {useCase.icon === "share" && <Share2 className="w-5 h-5 text-[#D4AF37]" />}
                  {useCase.icon === "globe" && <ExternalLink className="w-5 h-5 text-[#D4AF37]" />}
                </div>
                <h3 className="font-medium text-sm mb-1">{useCase.name}</h3>
                <p className="text-xs text-white/40">{useCase.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Simple Process</p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight">Three Steps to Stunning</h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { icon: Upload, num: "01", title: "Upload Your Selfie", desc: "Any photo works — smartphone selfie, webcam shot, or professional photo. Our AI handles the rest." },
              { icon: Image, num: "02", title: "Pick Your Styles", desc: "Choose from 12 iconic looks: LinkedIn Pro, Magazine Cover, Red Carpet, Fantasy, Anime & more." },
              { icon: Download, num: "03", title: "Download & Share", desc: "Your AI portraits are ready in seconds. Download HD, share on social media, or gift to friends." }
            ].map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="text-center"
              >
                <p className="text-[#D4AF37] font-serif text-5xl mb-4 opacity-30">{step.num}</p>
                <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center border border-[#D4AF37]/30 bg-[#D4AF37]/5 rounded-xl">
                  <step.icon className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 className="font-serif text-2xl mb-3">{step.title}</h3>
                <p className="text-white/50 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <button onClick={() => onNavigate("/start")} className="btn-primary px-8">
              Get 3 Free Credits
            </button>
            <p className="text-white/30 text-xs mt-3">No credit card required</p>
          </div>
        </div>
      </section>
      
      {/* Referral Section */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="glass-card p-12 md:p-16 text-center relative overflow-hidden"
          >
            {/* Background glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/5 via-transparent to-[#D4AF37]/5" />
            
            <div className="relative z-10">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                <Gift className="w-8 h-8 text-[#D4AF37]" />
              </div>
              
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Refer & Earn</p>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-6">
                Invite Friends, <span className="gold-text">Get 3 Free Credits</span>
              </h2>
              <p className="text-lg text-white/60 max-w-2xl mx-auto mb-8">
                Share your referral link with friends. When they sign up, you earn 3 bonus credits!
              </p>
              
              <div className="flex flex-wrap justify-center gap-4">
                <button 
                  onClick={() => onNavigate("/start")}
                  className="flex items-center gap-3 px-6 py-3 bg-white/5 border border-white/10 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/30 transition-all duration-200 cursor-pointer"
                  data-testid="landing-share-link-btn"
                >
                  <Share2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-sm">Share Your Link</span>
                </button>
                <div className="flex items-center gap-3 px-6 py-3 bg-white/5 border border-white/10">
                  <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-sm">Friend Signs Up</span>
                </div>
                <div className="flex items-center gap-3 px-6 py-3 bg-white/5 border border-white/10">
                  <Crown className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-sm">You Get 3 Credits</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight mb-6">
              Ready to Look <span className="gold-text">Legendary</span>?
            </h2>
            <p className="text-lg text-white/60 mb-10">
              Join thousands of athletes, artists, and executives who trust StarFrame.
            </p>
            <button 
              onClick={() => onNavigate("/start")} 
              className="btn-primary inline-flex items-center gap-2"
              data-testid="cta-get-started"
            >
              Get 3 Free Credits
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};


